import React, { useContext } from 'react';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import 'bulma/css/bulma.min.css';

import { Parameter } from '../scanTypes.ts'
import { BeamlineContext } from '../beamlineProvider.tsx'
import { InputAutocomplete, SearchFunctions, SearchResult, SelectingStatus } from './inputAutocomplete.tsx'
import { Uid, highlightSearchTermsInString } from "../utils.tsx";
import './parameterAutocomplete.css';


interface ParameterSearchFunctions {
  itemSelected: (item: Parameter) => SelectingStatus;
  selectedNone: () => SelectingStatus;
}


// Settings passed in with the React component
interface ParameterAutocompleteParameters {
  selectedItem: Parameter | null;
  searchFunctions: ParameterSearchFunctions;
  allowedParameters?: Uid[];
  inputSize?: string;
  iconSize?: SizeProp;
  isReadOnly?: boolean;
  greenWhenValid?: boolean;
  triggerFocus?: boolean;
  resetOnDefocus?: boolean;
}


function ParameterAutocomplete(settings:ParameterAutocompleteParameters) {

  const beamlineContext = useContext(BeamlineContext);

  async function searchParameters(searchString: string): Promise<SearchResult<Parameter>> {
    const searchStringLower = searchString.toLowerCase();
    // If allowedParameters is an array, we use it even if it's empty.
    const uidsToTest = Array.isArray(settings.allowedParameters) ? settings.allowedParameters : beamlineContext.scanTypes.parameterIdsInDisplayOrder;
    const toTest = uidsToTest.map((u) => beamlineContext.scanTypes.parametersById.get(u)!);
    const matches = toTest.filter((p) =>
                      p.name.toLowerCase().includes(searchStringLower) ||
                      p.description.toLowerCase().includes(searchStringLower));
    return { matches: matches };
  };

  function renderMatch(item: Parameter, searchString: string): React.JSX.Element {
    return (<>
              <td>{ highlightSearchTermsInString(item.name, [searchString]) }</td>
              <td>{ highlightSearchTermsInString(item.description, [searchString]) }</td>
            </>);
  }

  const parameterSearchFunctions: SearchFunctions<Parameter> = {
    itemToString: (item) => { return item.name },
    itemsRetriever: searchParameters,
    itemSelected: settings.searchFunctions.itemSelected,
    selectedNone: settings.searchFunctions.selectedNone,
    renderMatchedItem: renderMatch
  };

  return InputAutocomplete<Parameter>({
      value: settings.selectedItem ? settings.selectedItem.name : "",
      selectedItem: settings.selectedItem,
      searchFunctions: parameterSearchFunctions,
      addedClass: "parameter",
      inputSize: settings.inputSize,
      iconSize: settings.iconSize,
      placeholder: "Select a Parameter",
      isReadOnly: settings.isReadOnly,
      greenWhenValid: settings.greenWhenValid,
      resetOnDefocus: settings.resetOnDefocus,
      triggerFocus: settings.triggerFocus,
      showAllByDefault: true,
      renderResultsAsTable: true
    });
}

export { ParameterAutocomplete }
export type { ParameterSearchFunctions }
